import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from './api';
import type { User, UserNotification, BilingualText } from '@/types';

/** Imported on demand: the two stores would otherwise import each other at module load. */
async function refreshShopAccess(): Promise<void> {
  const { useAdminStore } = await import('./admin-store');
  await useAdminStore.getState().refreshSettings();
}


interface UserState {
  currentUser: User | null;
  token: string | null;
  guestWishlist: string[];

  register: (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    birthDate?: string;
    password: string;
  }) => Promise<{ user: User } | { error: 'EMAIL_IN_USE' | 'RATE_LIMITED' | 'VALIDATION_ERROR' | 'UNKNOWN'; message: string }>;

  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  restoreSession: () => Promise<void>;

  updateProfile: (updates: Partial<Pick<User, 'firstName' | 'lastName' | 'email' | 'phone' | 'birthDate' | 'address' | 'shippingAddress' | 'billingAddress'>>) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<boolean>;

  toggleWishlist: (artworkId: string) => void;
  isInWishlist: (artworkId: string) => boolean;

  toggleGuestWishlist: (artworkId: string) => void;
  isInGuestWishlist: (artworkId: string) => boolean;

  requestPasswordReset: (email: string) => Promise<boolean | 'NOT_REGISTERED' | 'RATE_LIMITED'>;
  resetPassword: (email: string, token: string, newPassword: string) => Promise<boolean>;

  addNotification: (userId: string, notification: { type: UserNotification['type']; orderId?: string; orderLabel?: string; conversationId?: string; message: BilingualText }) => void;
  markNotificationRead: (notificationId: string) => void;
  markAllNotificationsRead: () => void;

  getUsers: () => User[];
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      currentUser: null,
      token: null,
      guestWishlist: [],

      register: async (data) => {
        try {
          const result = await authApi.register(data);
          const user = result.user as User;
          const token = result.token;

          localStorage.setItem('gallery-auth-token', token);
          set({ currentUser: user, token, guestWishlist: [] });

          // Merge guest wishlist
          const guestWl = get().guestWishlist;
          for (const id of guestWl) {
            authApi.toggleWishlist(id).catch(() => {});
          }

          return { user };
        } catch (err: unknown) {
          console.error('Registration failed:', err);
          const e = err as { code?: string; message?: string };
          if (e?.code === 'CONFLICT') return { error: 'EMAIL_IN_USE', message: e.message || 'Email already in use' };
          if (e?.code === 'RATE_LIMITED' || e?.code === 'EMAIL_COOLDOWN') return { error: 'RATE_LIMITED', message: e.message || 'Too many attempts. Try later.' };
          if (e?.code === 'VALIDATION_ERROR') return { error: 'VALIDATION_ERROR', message: e.message || 'Invalid input' };
          return { error: 'UNKNOWN', message: e?.message || 'Registration failed' };
        }
      },

      login: async (email, password) => {
        try {
          const result = await authApi.login(email, password);
          const user = result.user as User;
          const token = result.token;

          localStorage.setItem('gallery-auth-token', token);

          // Merge guest wishlist
          const guestWl = get().guestWishlist;
          for (const id of guestWl) {
            authApi.toggleWishlist(id).catch(() => {});
          }

          set({ currentUser: user, token, guestWishlist: [] });
          // Whether the shop is open is decided per visitor, so the answer has to be
          // re-asked now that there is one. Without this a whitelisted member had to reload
          // the page before the shop appeared.
          void refreshShopAccess();
          return true;
        } catch {
          return false;
        }
      },

      logout: () => {
        localStorage.removeItem('gallery-auth-token');
        set({ currentUser: null, token: null });
        // Same in reverse: the shop must close again straight away, not at the next reload.
        void refreshShopAccess();
      },

      restoreSession: async () => {
        const token = localStorage.getItem('gallery-auth-token');
        if (!token) return;

        try {
          const user = await authApi.getMe() as User;
          set({ currentUser: user, token });
        } catch {
          localStorage.removeItem('gallery-auth-token');
          set({ currentUser: null, token: null });
        }
      },

      updateProfile: async (updates) => {
        if (!get().currentUser) throw new Error('Please sign in again');
        const user = await authApi.updateProfile(updates);
        set({ currentUser: user as User });
      },

      changePassword: async (oldPassword, newPassword) => {
        try {
          await authApi.changePassword(oldPassword, newPassword);
          return true;
        } catch {
          return false;
        }
      },

      toggleWishlist: (artworkId) => {
        const state = get();
        if (!state.currentUser) return;

        // Optimistic update
        const wishlist = state.currentUser.wishlist.includes(artworkId)
          ? state.currentUser.wishlist.filter((id) => id !== artworkId)
          : [...state.currentUser.wishlist, artworkId];
        set({ currentUser: { ...state.currentUser, wishlist } });

        // Sync to server
        authApi.toggleWishlist(artworkId).then(({ wishlist: serverWl }) => {
          const cur = get().currentUser;
          if (cur) set({ currentUser: { ...cur, wishlist: serverWl } });
        }).catch(() => {});
      },

      isInWishlist: (artworkId) => {
        return get().currentUser?.wishlist.includes(artworkId) ?? false;
      },

      toggleGuestWishlist: (artworkId) => {
        set((s) => ({
          guestWishlist: s.guestWishlist.includes(artworkId)
            ? s.guestWishlist.filter((id) => id !== artworkId)
            : [...s.guestWishlist, artworkId],
        }));
      },

      isInGuestWishlist: (artworkId) => {
        return get().guestWishlist.includes(artworkId);
      },

      requestPasswordReset: async (email) => {
        try {
          const res = await fetch('/api/v1/auth/forgot-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.trim() }),
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok) return true;
          // Return error code as string so frontend can show specific messages
          if (data?.error?.code === 'EMAIL_NOT_REGISTERED') return 'NOT_REGISTERED';
          if (data?.error?.code === 'RATE_LIMITED') return 'RATE_LIMITED';
          return false;
        } catch {
          return false;
        }
      },

      resetPassword: async (email, token, newPassword) => {
        try {
          const res = await fetch('/api/v1/auth/reset-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.trim(), token, newPassword }),
          });
          return res.ok;
        } catch {
          return false;
        }
      },

      addNotification: (_userId, _notification) => {
        // TODO: notifications managed server-side
      },

      markNotificationRead: (notificationId) => {
        set((s) => {
          if (!s.currentUser) return s;
          const notifications = (s.currentUser.notifications ?? []).map((n) =>
            n._id === notificationId ? { ...n, read: true } : n,
          );
          return { currentUser: { ...s.currentUser, notifications } };
        });
      },

      markAllNotificationsRead: () => {
        set((s) => {
          if (!s.currentUser) return s;
          const notifications = (s.currentUser.notifications ?? []).map((n) => ({ ...n, read: true }));
          return { currentUser: { ...s.currentUser, notifications } };
        });
      },

      getUsers: () => {
        const user = get().currentUser;
        return user ? [user] : [];
      },
    }),
    {
      name: 'gallery-user-v3',
      partialize: (state) => ({
        currentUser: state.currentUser,
        token: state.token,
        guestWishlist: state.guestWishlist,
      }),
    },
  ),
);
